import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../lib/auth';
import type { AuthResponse } from '../../lib/auth';
import { API_BASE } from '../../lib/apiBase';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
  letterSpacing: '0.15em',
  textAlign: 'center',
};

interface LocationState {
  email: string;
  password: string;
}

export function TwoFactorLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);

  const state = location.state as LocationState | null;

  const [code, setCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If navigated to directly without credentials, back to login
  if (!state?.email || !state?.password) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/auth/2fa/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: state.email,
          password: state.password,
          code: code.trim().replace(/\s/g, ''),
          is_backup_code: isBackupCode,
        }),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: { message: string } };
        setError(err.error?.message ?? 'Verification failed. Please try again.');
        return;
      }

      const body = await res.json() as AuthResponse;
      setAuth(body.user, body.access_token, body.expires_in);
      navigate('/app/dashboard', { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#f9fafb',
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: '#1D4ED8', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28,
          }}>
            🔐
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            {isBackupCode ? 'Use a backup code' : 'Two-step verification'}
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 6, marginBottom: 0 }}>
            {isBackupCode
              ? 'Enter one of your 10-character backup codes.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <input
              type={isBackupCode ? 'text' : 'tel'}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={isBackupCode ? 'XXXXX-XXXXX' : '000000'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={isBackupCode ? 11 : 6}
              autoFocus
              style={inputStyle}
            />
          </div>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, padding: '10px 14px',
              color: '#dc2626', fontSize: 14, textAlign: 'center',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !code.trim()}
            style={{
              width: '100%', padding: '14px',
              background: isSubmitting || !code.trim() ? '#93c5fd' : '#1D4ED8',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Verifying…' : 'Verify'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: '#6b7280' }}>
          <button
            type="button"
            onClick={() => { setIsBackupCode(!isBackupCode); setCode(''); setError(''); }}
            style={{
              background: 'none', border: 'none', color: '#1D4ED8',
              cursor: 'pointer', fontWeight: 500, fontSize: 14,
            }}
          >
            {isBackupCode ? 'Use authenticator app instead' : "Can't access your app? Use a backup code"}
          </button>
        </p>

        <p style={{ textAlign: 'center', marginTop: 8, fontSize: 14, color: '#6b7280' }}>
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={{
              background: 'none', border: 'none', color: '#6b7280',
              cursor: 'pointer', fontSize: 14,
            }}
          >
            Back to sign in
          </button>
        </p>
      </div>
    </div>
  );
}
